<script setup lang="tsx">
import { ref, watch } from 'vue'
import Autocomplete from '../widgets/Autocomplete.vue'
import type { Asset, AssetWithConnections, BusinessProcess, ModuleConnection, Response } from '@/models/api-generated'
import { genRouteID } from '@/services/utils'
import CancelSaveButtons from '../widgets/CancelSaveButtons.vue'
import sliderStore from '@/stores/slider'
import toastStore from '@/stores/toast'
import http from '@/services/http'
import { assetRefreshCallback } from '@/components/business_processes/shared'
import { useI18n } from 'vue-i18n'
import { TagModule, TagBP } from '@/models/enums-generated'

const { t } = useI18n()
const slider = sliderStore()
const toaster = toastStore()
const props = defineProps<{ canEdit: boolean; isChangeMode: boolean }>()
const model = defineModel<AssetWithConnections>({ required: true })
const selectedModule = ref<ModuleConnection>()
const selectedBP = ref<BusinessProcess>()
const isConnectionEdit = ref(false)
const canSave = ref(true)
const emit = defineEmits(['cancel-edit'])

const editBtns = <CancelSaveButtons saveId="save-asset" cancelId="cancel-asset" canSave={canSave} onCancel={cancel} onSave={save} />

watch(selectedModule, (newModule) => {
	if (newModule && !model.value?.modules.some((a) => a.id === newModule.id)) {
		model.value?.modules.push(newModule)
		selectedModule.value = undefined
	}
})
watch(selectedBP, (newBP) => {
	if (newBP && !model.value?.businessProcesses.some((bp) => bp.id === newBP.id)) {
		model.value?.businessProcesses.push(newBP)
		selectedBP.value = undefined
	}
})

async function save() {
	if (!model.value) return
	const data = {
		bpIds: model.value.businessProcesses.map((a) => a.id),
		modules: model.value.modules.map((a) => ({
			moduleId: a.id,
			metaId: a.metaId,
		})),
	}
	const url = `/api/asset/editConnections/${genRouteID(model.value.asset.id, '', 'asset')}`

	const { status } = await http.post<Response<Asset>>(url, data)
	if (status === 200) {
		toaster.showSuccess(t('misc.successfullyEdited'))
		slider.setUnsavedChanges(false)
		slider.close()
		assetRefreshCallback.value?.forEach((fn) => fn())
	}
}

async function cancel() {
	isConnectionEdit.value = false
	emit('cancel-edit')
}

function removeConnection(type: string, id: number) {
	if (!model.value) return
	if (type === TagModule) {
		model.value.modules = model.value.modules.filter((a) => a.id !== id)
	}
	if (type === TagBP) {
		model.value.businessProcesses = model.value.businessProcesses.filter((bp) => bp.id !== id)
	}
}

function setMode() {
	isConnectionEdit.value = !isConnectionEdit.value
	slider.setFooter(editBtns)
}

function goToDataAssetPage(name: string) {
	const params = new URLSearchParams()
	params.append(`name`, name)
	window.location.href = `/app/dataassets?${params}`
}

function goToModulePage(name: string) {
	const params = new URLSearchParams()
	params.append(`code`, name)
	window.location.href = `/app/modules?${params}`
}
</script>

<template>
	<template v-if="model">
		<div v-if="!isChangeMode" class="pt-4 d-flex flex-sm-column flex-content-contain">
			<div class="row">
				<h6 class="col mt-2">{{ $t('connections') }}</h6>
				<div v-if="!isChangeMode && props.canEdit && !isConnectionEdit" class="col-8 d-flex justify-content-end mb-1">
					<button data-test="add-asset-connection" type="button" class="btn btn-outline-primary btn-sm" @click="setMode()">
						<i class="bi bi-link-45deg me-1"></i> {{ $t('asset.editConnections') }}
					</button>
				</div>
			</div>

			<hr class="mt-0" />

			<div v-if="isConnectionEdit" class="row mt-3">
				<div class="col-4">
					<label for="asset-autocomplete">{{ $t('chooseModule') }}</label>
				</div>
				<div class="col-8 d-flex justify-content-end">
					<Autocomplete
						v-model="selectedModule"
						id="module-select"
						:url="'/api/assetconnections'"
						:label="$t('misc.search')"
						:type="TagModule"
						:instance-id="model.asset.instance.id"
						:exlude-ids="model.modules.map((a) => a.id)"
						:valid-input="true"
					/>
				</div>
			</div>

			<div v-if="isConnectionEdit" class="row mt-3">
				<div class="col-4">
					<label for="bp-autocomplete">{{ $t('chooseBusinessProcess') }}</label>
				</div>
				<div class="col-8 d-flex justify-content-end">
					<Autocomplete
						v-model="selectedBP"
						id="bp-select"
						:url="'/api/assetconnections'"
						:label="$t('misc.search')"
						:type="TagBP"
						:instance-id="model.asset.instance.id"
						:exlude-ids="model.businessProcesses.map((bp) => bp.id)"
						:valid-input="true"
					/>
				</div>
			</div>

			<div v-for="process in model.businessProcesses" :key="process.id" class="row mt-3">
				<div class="col-4">{{ process.name }}</div>
				<div class="col-8 d-flex justify-content-end">
					<a v-one-tooltip="process.name" :href="`./app/bp/view/${genRouteID(process.id, '', 'BusinessProcess')}`" class="me-2">
						<i class="bi bi-info-circle-fill"></i>
					</a>
					<span v-one-tooltip="$t('businessProcess')" class="badge bg-primary me-2 fixed-badge">BP</span>

					<button
						v-if="isConnectionEdit"
						id="remove-bp-connection"
						@click="removeConnection('businessProcess', process.id)"
						class="btn-close"
						v-one-tooltip="$t('misc.remove')"
					></button>
				</div>
			</div>

			<div v-for="a in model.modules" :key="a.id" class="row mt-3">
				<div class="col-4">{{ a.description }} ({{ a.name }})</div>
				<div class="col-8 d-flex justify-content-end">
					<a v-one-tooltip="a.name" class="me-2">
						<i class="bi bi-info-circle-fill" @click="goToModulePage(a.name)"></i>
					</a>
					<span v-one-tooltip="$t('module.label')" class="badge bg-primary me-2 fixed-badge">MO</span>

					<button
						v-if="isConnectionEdit"
						id="remove-module-connection"
						@click="removeConnection('module', a.id)"
						class="btn-close"
						v-one-tooltip="$t('misc.remove')"
					></button>
				</div>
			</div>

			<div v-for="process in model.dataAssets" :key="process.id" class="row mt-3">
				<div class="col-4">{{ process.name }}</div>
				<div class="col-8 d-flex justify-content-end">
					<a v-one-tooltip="process.name" :title="process.name" class="me-2">
						<i class="bi bi-info-circle-fill" @click="goToDataAssetPage(process.name)"></i>
					</a>
					<span v-one-tooltip="$t('dataAsset')" class="badge bg-primary me-2 fixed-badge">DA</span>
				</div>
			</div>
		</div>
	</template>
</template>

<style lang="sass" scoped>
.fixed-badge
  max-height: 1.5rem
</style>
